import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { metricsByAffiliate, WINDOWS, type AffiliateMetrics } from "@/lib/affiliateMetrics";

type Affiliate = {
  id?: string;
  slug?: string;
  name: string;
  status: string;
  featured: boolean;
  category: string;
};

type Lead = {
  affiliate_id: string;
  status: string;
  created_at: string;
  updated_at?: string | null;
};

type SortKey =
  | "name"
  | "total"
  | "newCount"
  | "contacted"
  | "closed"
  | "conversionPct"
  | "avgResponseHours"
  | "lastActivity";

interface Props {
  affiliates: Affiliate[];
  leads: Lead[];
  onOpen: (affiliateId: string) => void;
}

const fmtPct = (n: number) => `${n.toFixed(0)}%`;
const fmtHours = (h: number | null) =>
  h == null ? "—" : h < 1 ? `${(h * 60).toFixed(0)}m` : `${h.toFixed(1)}h`;

const AffiliatePerformanceTable = ({ affiliates, leads, onOpen }: Props) => {
  const [windowDays, setWindowDays] = useState<number | null>(30);
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const rows = useMemo(() => {
    const m = metricsByAffiliate(leads, windowDays);
    return affiliates.map((a) => ({
      affiliate: a,
      metrics: (m[a.id ?? ""] ?? {
        total: 0, newCount: 0, contacted: 0, closed: 0,
        conversionPct: 0, responsePct: 0, avgResponseHours: null, lastActivity: null,
      }) as AffiliateMetrics,
    }));
  }, [affiliates, leads, windowDays]);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let av: number | string | null;
      let bv: number | string | null;
      if (sortKey === "name") { av = a.affiliate.name.toLowerCase(); bv = b.affiliate.name.toLowerCase(); }
      else if (sortKey === "lastActivity") {
        av = a.metrics.lastActivity ? new Date(a.metrics.lastActivity).getTime() : 0;
        bv = b.metrics.lastActivity ? new Date(b.metrics.lastActivity).getTime() : 0;
      } else if (sortKey === "avgResponseHours") {
        av = a.metrics.avgResponseHours ?? Number.MAX_SAFE_INTEGER;
        bv = b.metrics.avgResponseHours ?? Number.MAX_SAFE_INTEGER;
      } else {
        av = a.metrics[sortKey] as number;
        bv = b.metrics[sortKey] as number;
      }
      if (av! < bv!) return sortDir === "asc" ? -1 : 1;
      if (av! > bv!) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "name" ? "asc" : "desc"); }
  };

  const Th = ({ k, children, className }: { k: SortKey; children: React.ReactNode; className?: string }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`text-left text-[11px] uppercase tracking-wider text-muted-foreground font-semibold py-2 px-2 cursor-pointer hover:text-foreground ${className ?? ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className="h-3 w-3 opacity-60" />
      </span>
    </th>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display text-lg font-semibold">Provider performance</h3>
        <div className="flex gap-1.5">
          {WINDOWS.map((w) => (
            <Button
              key={w.label}
              size="sm"
              variant={windowDays === w.days ? "default" : "outline"}
              onClick={() => setWindowDays(w.days)}
            >
              {w.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-border">
              <Th k="name">Provider</Th>
              <Th k="total" className="text-right">Leads</Th>
              <Th k="newCount" className="text-right">New</Th>
              <Th k="contacted" className="text-right">Contacted</Th>
              <Th k="closed" className="text-right">Closed</Th>
              <Th k="conversionPct" className="text-right">Conv</Th>
              <Th k="avgResponseHours" className="text-right">Avg reply</Th>
              <Th k="lastActivity" className="text-right">Last activity</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ affiliate, metrics }) => (
              <tr
                key={affiliate.id}
                onClick={() => affiliate.id && onOpen(affiliate.id)}
                className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40"
              >
                <td className="py-2.5 px-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{affiliate.name}</span>
                    {affiliate.featured ? <Badge variant="outline" className="text-[10px]">★</Badge> : null}
                    {affiliate.status !== "active" ? (
                      <Badge variant="secondary" className="text-[10px] capitalize">{affiliate.status}</Badge>
                    ) : null}
                  </div>
                </td>
                <td className="py-2.5 px-2 text-right tabular-nums">{metrics.total}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{metrics.newCount}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{metrics.contacted}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{metrics.closed}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{metrics.total ? fmtPct(metrics.conversionPct) : "—"}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{fmtHours(metrics.avgResponseHours)}</td>
                <td className="py-2.5 px-2 text-right text-muted-foreground">
                  {metrics.lastActivity
                    ? formatDistanceToNow(new Date(metrics.lastActivity), { addSuffix: true })
                    : "—"}
                </td>
              </tr>
            ))}
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                  No providers yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AffiliatePerformanceTable;